"use client";

import { useState, useEffect, useRef } from "react";
import { formatDistance } from "date-fns";
import { API_BASE_URL } from "@/lib/config";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  FileText,
  AlertTriangle,
} from "lucide-react";
import { NotebookCell } from "@/components/notebook/NotebookCell";
import useSWR from "swr";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/lib/api-client";
import { toast } from "sonner";
import { useSWRConfig } from "swr";
import { cn } from "@/lib/utils";

const fetcher = (url: string) => fetch(url, { credentials: "include" }).then((res) => res.json());

interface RunDetailsProps {
  sessionId: string;
  onBackToRuns: () => void;
  onBackToPlan: () => void;
  cellsExecuted?: number;
  onCellsUpdate?: (cells: any[]) => void;
  onCurrentCellChange?: (cellId: string) => void;
  isRerunning?: boolean;
  onRerunComplete?: (status: "success" | "error") => void;
  onResumePolling?: () => void;
  onEditCell?: (index: number, purpose: string) => void;
  onNotebookIdChange?: (notebookId: string | null) => void;
  onSummaryChange?: (summary: string | null) => void;
  onUpdatedAtChange?: (updatedAt: number | null) => void;
  isReadonly?: boolean;
  isForkedWorkflow?: boolean;
  chatStatus?: "submitted" | "streaming" | "ready" | "error";
  isReplay?: boolean;
  focusedCellId?: string;
  focusedCellAnimationKey?: number;
}

export function RunDetails({
  sessionId,
  onBackToRuns,
  onBackToPlan,
  cellsExecuted = 0,
  onCellsUpdate,
  onCurrentCellChange,
  isRerunning = false,
  onRerunComplete,
  onResumePolling,
  onEditCell,
  onNotebookIdChange,
  onSummaryChange,
  onUpdatedAtChange,
  isReadonly = false,
  isForkedWorkflow = false,
  chatStatus = "ready",
  isReplay = false,
  focusedCellId,
  focusedCellAnimationKey = 0,
}: RunDetailsProps) {
  const { user } = useAuth();
  const { mutate } = useSWRConfig();
  const hasLoadedRef = useRef(false);

  // For forked workflows, delay fetch until streaming has started (clone is complete)
  // Once we have data, never disable fetching again
  const shouldFetch =
    isReplay || !isForkedWorkflow || chatStatus !== "submitted" || hasLoadedRef.current;

  const replayUrl = `${API_BASE_URL}/replay/notebook/${sessionId}`;
  const normalUrl = `${API_BASE_URL}/strategies/active-run-details/${sessionId}`;
  const fetchUrl = isReplay ? replayUrl : normalUrl;

  // Track previous chatStatus to detect streaming→ready transition for final refetch
  const prevChatStatusRef = useRef(chatStatus);
  const replayJustFinishedRef = useRef(false);

  useEffect(() => {
    if (isReplay) {
    }
    if (isReplay && prevChatStatusRef.current === "streaming" && chatStatus !== "streaming") {
      // Streaming just ended — force immediate refetch + keep polling briefly for any stragglers
      replayJustFinishedRef.current = true;
      mutate(fetchUrl);
      const timer = setTimeout(() => {
        replayJustFinishedRef.current = false;
        mutate(fetchUrl); // One more final fetch after cooldown
      }, 3000);
      return () => clearTimeout(timer);
    }
    prevChatStatusRef.current = chatStatus;
  }, [chatStatus, isReplay, mutate, fetchUrl]);

  const { data: runData } = useSWR(
    shouldFetch ? fetchUrl : null,
    fetcher,
    {
      // Poll every 500ms when rerunning or streaming (agent adding cells)
      // For replay: also poll briefly after streaming ends to catch final cells
      refreshInterval: (isRerunning || chatStatus === "streaming" || replayJustFinishedRef.current) ? (isReplay ? 2500 : 500) : 0,
      // Disable automatic revalidation - we control updates explicitly
      revalidateOnFocus: false,
      revalidateOnReconnect: false,

      // Detect completion (status can be "success", "error", "idle", "canceled", or "executing")
      onSuccess: (data) => {
        // Mark as loaded so we never disable fetching again
        if (data) hasLoadedRef.current = true;
        if (isReplay) {
          return;
        }
        // Resume polling if backend is executing but UI doesn't know (e.g., after refresh)
        if (data?.status === "executing" && !isRerunning && onResumePolling) {
          onResumePolling();
        }

        // Stop polling if status is anything other than "executing"
        if (data?.status !== "executing" && isRerunning && onRerunComplete) {
          const status = data?.status === "success" ? "success" : "error";
          onRerunComplete(status);
        }
      },
    }
  );

  // Poll for sub-notebook progress when notebook is executing OR chat is streaming
  // Fund sub-agents run AFTER main agent finishes streaming, so we need to check notebook status too
  const isNotebookExecuting = runData?.status === "executing";
  const shouldPollSubProgress = chatStatus === "streaming" || isNotebookExecuting;
  
  const subProgressUrl = isReplay
    ? `${API_BASE_URL}/replay/sub-notebook-progress/${sessionId}`
    : `${API_BASE_URL}/strategies/sub-notebook-progress/${sessionId}`;

  const { data: subProgress } = useSWR(
    shouldPollSubProgress
      ? subProgressUrl
      : null,
    fetcher,
    {
      refreshInterval: 2000, // Poll every 2s during streaming
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess: () => {},
    }
  );

  // Handle individual cell execution
  const handleRunCell = async (cellIndex: number) => {
    if (!runData?.id) {
      toast.error("Notebook ID not available");
      return;
    }

    if (isReadonly) {
      toast.error(
        "You don't have permission to execute cells in this notebook"
      );
      return;
    }

    if (isRerunning) {
      toast.error("Cannot run cell while notebook is rerunning");
      return;
    }

    try {
      // Backend blocks until execution completes
      const result = await apiClient.executeCellByIndex(runData.id, cellIndex);

      // Invalidate SWR cache to refresh the data
      await mutate(
        `${API_BASE_URL}/strategies/active-run-details/${sessionId}`
      );

      toast.success(`Cell ${cellIndex} executed successfully`);
    } catch (error) {
      toast.error("Cell execution failed");
    }
  };

  // Send cells data to parent
  useEffect(() => {
    if (runData?.cells && onCellsUpdate) {
      onCellsUpdate(runData.cells);
    }
  }, [runData?.cells, onCellsUpdate]);

  // Send notebook_id to parent
  useEffect(() => {
    if (onNotebookIdChange) {
      onNotebookIdChange(runData?.id || null);
    }
  }, [runData?.id, onNotebookIdChange]);

  // Send summary to parent
  useEffect(() => {
    if (onSummaryChange) {
      onSummaryChange(runData?.summary || null);
    }
  }, [runData?.summary, onSummaryChange]);

  // Send updatedAt to parent
  useEffect(() => {
    if (onUpdatedAtChange) {
      onUpdatedAtChange(runData?.updatedAt || null);
    }
  }, [runData?.updatedAt, onUpdatedAtChange]);

  // Auto-scroll to latest cell when new cells are added
  useEffect(() => {
    if (runData?.cells?.length > 0) {
      const latestCell = runData.cells[runData.cells.length - 1];
      setTimeout(() => {
        const cellElement = document.getElementById(`cell-${latestCell.id}`);
        if (cellElement) {
          cellElement.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      }, 100);
    }
  }, [runData?.cells?.length]);

  if (!runData) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-8 text-muted-foreground">
        <Clock className="h-12 w-12 mx-auto mb-4 opacity-50 animate-spin" />
        <p>Loading workflow details...</p>
      </div>
    );
  }

  if (!runData.cells || runData.cells.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-8 text-muted-foreground">
        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No execution steps found for this workflow.</p>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full p-2">
      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        {runData.cells.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No execution steps found for this workflow.</p>
          </div>
        ) : (
          <div className="py-0 mt-2 space-y-2">
            {runData.cells.map((cell: any, cellIdx: number) => {
              const cellSubProgress = subProgress?.cells_with_sub_agents?.find(
                (c: any) => c.cell_index === cell.index
              );

              return (
                <div
                  key={`${cell.id}:${cell.status}`}
                  id={`cell-${cell.id}`}
                  className={cn(
                    "rounded-xl transition-all duration-300",
                    focusedCellId === cell.id &&
                      (focusedCellAnimationKey % 2 === 0
                        ? "citation-cell-highlight-a"
                        : "citation-cell-highlight-b")
                  )}
                >
                  <NotebookCell
                    cell={cell}
                    isEditButtonShown={true}
                    onEditCell={
                      onEditCell
                        ? () => onEditCell(cell.index, cell.purpose)
                        : undefined
                    }
                    onRunCell={handleRunCell}
                    isRerunning={isRerunning}
                    isReadonly={isReadonly}
                    subProgress={cellSubProgress}
                    isReplay={isReplay}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
